/** IXD Preview 셀 식별/편집 lookup 헬퍼 (순수 함수, React 비의존) */

import type { IxdChunk, PeriodType } from "@/apis/compare/route";
import {
  findContentTaggedValue,
  getDlViewContentText,
} from "@/utils/ixd-editor/content-display";
import {
  buildCellIdFromTaggedValue,
  buildCellIdFromTableColumn,
  buildCellIdLookupCandidates,
  normalizeCellId,
} from "@/utils/ixd-cell-model";
import type { TableCellColumn } from "../table-cell";

export type IxdDlView = IxdChunk["dlViews"][number];

export interface CellIdentityLookupInfo {
  normalizedCellId?: string;
  identityKey?: string;
  canonicalCellId?: string | null;
  candidates: string[];
  matchedCandidate?: string;
  matchedSource?: "dlView.values" | "session";
}

export const getCellIdentityLookupInfo = (
  periodType: PeriodType,
  column: TableCellColumn,
  dlViewIdentityByNormalizedCellId?: Map<string, string>,
  cellIdentityByNormalizedCellId?: Map<string, string>,
): CellIdentityLookupInfo => {
  if (!column.id) return { candidates: [] };
  const canonicalCellId = buildCellIdFromTableColumn(periodType, column);
  const candidates = buildCellIdLookupCandidates(
    periodType,
    canonicalCellId,
    column.id,
    column.attributes.contextKey,
  );

  for (const candidate of candidates) {
    const dlViewIdentityKey = dlViewIdentityByNormalizedCellId?.get(candidate);
    if (dlViewIdentityKey) {
      return {
        normalizedCellId: candidate,
        identityKey: dlViewIdentityKey,
        canonicalCellId,
        candidates,
        matchedCandidate: candidate,
        matchedSource: "dlView.values",
      };
    }

    const sessionIdentityKey = cellIdentityByNormalizedCellId?.get(candidate);
    if (sessionIdentityKey) {
      return {
        normalizedCellId: candidate,
        identityKey: sessionIdentityKey,
        canonicalCellId,
        candidates,
        matchedCandidate: candidate,
        matchedSource: "session",
      };
    }
  }
  return { normalizedCellId: candidates[0], canonicalCellId, candidates };
};

export interface ContentEditInfo {
  cellId: string;
  normalizedCellId: string;
}

export const getContentEditInfo = (
  dlView: IxdChunk["dlViews"][number],
): ContentEditInfo | null => {
  const matched = findContentTaggedValue(
    dlView.values,
    getDlViewContentText(dlView),
  );
  if (!matched) return null;

  const cellId = buildCellIdFromTaggedValue(
    matched.periodType,
    matched.taggedValue,
  );
  return { cellId, normalizedCellId: normalizeCellId(cellId) };
};
